// SiblingsNav — detail page 「上一個 / 下一個」 siblings (W3.1 §5.2).
//
// Filter cascade: same category → same author → global nearest.

import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

import {
  shouldRenderSiblings,
  siblingsOf,
} from "@/lib/anniversary/siblingsNav";
import { countdownLabel, daysUntil } from "@/lib/anniversary/countdown";
import type { AnniversaryListItem } from "@/lib/api/anniversary";
import { cn } from "@/lib/utils/cn";

export interface SiblingsNavProps {
  list: AnniversaryListItem[];
  currentId: string;
}

export function SiblingsNav({ list, currentId }: SiblingsNavProps) {
  if (!shouldRenderSiblings(list)) return null;
  const { prev, next } = siblingsOf({ list, currentId });

  return (
    <section
      data-component="SiblingsNav"
      aria-label="相關紀念日"
      className="grid grid-cols-2 gap-3"
      data-testid="siblings-nav"
    >
      <SiblingCard
        side="prev"
        item={prev}
        emptyLabel="已是最早"
      />
      <SiblingCard
        side="next"
        item={next}
        emptyLabel="已是最新"
      />
    </section>
  );
}

interface SiblingCardProps {
  side: "prev" | "next";
  item: AnniversaryListItem | null;
  emptyLabel: string;
}

function SiblingCard({ side, item, emptyLabel }: SiblingCardProps) {
  const Icon = side === "prev" ? ChevronLeft : ChevronRight;

  if (!item) {
    // iOS-style disabled card: no border, muted text + faded chevron,
    // matches Apple's "no more content" treatment in list rows.
    return (
      <div
        data-testid={`sibling-${side}`}
        data-empty="true"
        aria-disabled="true"
        className={cn(
          "h-20 rounded-md bg-paper-calm/60 flex items-center gap-2 px-3",
          "text-meta text-ink-3 select-none",
        )}
      >
        {side === "prev" ? <Icon size={16} aria-hidden className="text-ink-3/50" /> : null}
        <span className="flex-1 text-center">{emptyLabel}</span>
        {side === "next" ? <Icon size={16} aria-hidden className="text-ink-3/50" /> : null}
      </div>
    );
  }

  const days = item.days_until_next ?? daysUntil(item.next_occurrence_date ?? item.date);

  return (
    <Link
      to={`/anniversaries/${item.id}`}
      data-testid={`sibling-${side}`}
      className={cn(
        "h-20 rounded-md glass border border-hairline p-3 flex items-center gap-2 press",
        "hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2",
      )}
    >
      {side === "prev" ? <Icon size={16} aria-hidden className="text-ink-2" /> : null}
      <div className="flex-1 min-w-0 flex flex-col">
        <p className="text-meta text-ink-3 truncate">{item.title}</p>
        <p className="text-body-sm text-ink-1 tabular-nums">{countdownLabel(days)}</p>
      </div>
      {side === "next" ? <Icon size={16} aria-hidden className="text-ink-2" /> : null}
    </Link>
  );
}